"use client";

import React from "react";
import { CircleAlert, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import {
  AddToCartButton,
  EcommerceProductsProvider,
  useEcommerceProducts,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";

function SingleProductConsumer() {
  const { products, loading, error } = useEcommerceProducts();

  if (loading) {
    return (
      <div data-section-id="233746" className="flex min-h-[60vh] items-center justify-center">
        <Loader className="size-10 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 text-destructive">
        <CircleAlert className="size-12" />
        <p className="text-lg font-medium font-default">Failed to load analysis details.</p>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <p className="text-lg text-muted-foreground font-default">Analysis product not found.</p>
      </div>
    );
  }

  const product = products[0];

  return (
    <div className="container mx-auto px-4">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
        {}
        <div className="space-y-4">
          <div className="aspect-square overflow-hidden rounded-sm bg-card border border-border shadow-md">
            {product.featured_image_url ? (
              <img
                src={product.featured_image_url}
                alt={product.title}
                className="h-full w-full object-cover object-center"
                loading="eager"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-muted">
                <span className="text-muted-foreground font-default">No preview available</span>
              </div>
            )}
          </div>
        </div>

        {}
        <div className="flex flex-col space-y-8">
          <div className="space-y-4">
            <h1 className="font-default text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl text-foreground">
              {product.title}
            </h1>

            <div className="flex items-center gap-4">
              {product.discounted_price ? (
                <div className="flex items-baseline gap-3">
                  <span className="font-default text-3xl font-bold text-primary">
                    ${product.discounted_price.toFixed(2)}
                  </span>
                  <span className="text-xl text-muted-foreground line-through font-default">
                    ${product.price.toFixed(2)}
                  </span>
                </div>
              ) : (
                <span className="font-default text-3xl font-bold text-primary">
                  ${product.price?.toFixed(2)}
                </span>
              )}
            </div>

            {product.short_description && (
              <div
                className="prose prose-sm dark:prose-invert text-muted-foreground font-default [&>p]:mb-4 last:[&>p]:mb-0"
                dangerouslySetInnerHTML={{ __html: product.short_description }}
              />
            )}
          </div>

          {}
          <div className="rounded-sm bg-card p-6 border border-border shadow-sm">
            <AddToCartButton
              productId={product.id}
              showQuantity={true}
              className="w-full"
              buttonClassName="bg-primary text-primary-foreground hover:bg-primary/90 w-full h-12 text-base font-bold font-default rounded-sm transition-all"
              text="Order Analysis"
            />
          </div>

          {}
          <div className="space-y-5 border-t border-border pt-6">
            {product.categories && product.categories.length > 0 && (
              <div className="flex items-start gap-3">
                <span className="text-sm font-bold text-foreground pt-1 w-24 shrink-0 font-default">Service Type:</span>
                <div className="flex flex-wrap gap-2">
                  {product.categories.map((category, i) => (
                    <Link key={category.id} to={category.link} data-index={i}>
                      <Badge variant="secondary" className="hover:bg-secondary/80 cursor-pointer transition-colors font-default rounded-none">
                        {category.name}
                      </Badge>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {product.tags && product.tags.length > 0 && (
              <div className="flex items-start gap-3">
                <span className="text-sm font-bold text-foreground pt-1 w-24 shrink-0 font-default">Capabilities:</span>
                <div className="flex flex-wrap gap-2">
                  {product.tags.map((tag, i) => (
                    <Link key={tag.id} to={tag.link} data-index={i}>
                      <Badge variant="outline" className="hover:bg-accent hover:text-accent-foreground cursor-pointer transition-colors font-default rounded-none">
                        {tag.name}
                      </Badge>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {}
      <div className="mt-16 grid grid-cols-1 gap-12 lg:grid-cols-3 border-t border-border pt-16">
        <div className="lg:col-span-2 space-y-8">
          {product.long_description && (
            <div>
              <h2 className="font-default text-2xl font-bold mb-6 text-foreground">Analysis Deliverables</h2>
              <div
                className="prose prose-base dark:prose-invert max-w-none text-muted-foreground font-default"
                dangerouslySetInnerHTML={{ __html: product.long_description }}
              />
            </div>
          )}
        </div>

        <div className="lg:col-span-1">
          {product.customFields && Object.keys(product.customFields).length > 0 && (
            <div className="sticky top-24">
              <h2 className="font-default text-2xl font-bold mb-6 text-foreground">Technical Specs</h2>
              <Accordion type="single" collapsible className="w-full bg-card rounded-sm border border-border px-4 shadow-sm">
                {Object.entries(product.customFields).map(([key, value], i) => (
                  <AccordionItem key={key} value={`item-${i}`} data-index={i} className="border-border">
                    <AccordionTrigger className="text-base font-bold capitalize hover:no-underline hover:text-primary transition-colors font-default">
                      {key.replace(/_/g, ' ')}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed font-default">
                      {String(value)}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SingleProductTemplateSection() {
  const current_wp_query_params = typeof window !== 'undefined' && window.wvcClient ? window.wvcClient.getCurrentWPQueryParams() : {};
  const wp_query = new WP_Query(current_wp_query_params);

  return (
    <section id="single-product-template-section" className="bg-background text-foreground py-16 md:py-24">
      <EcommerceProductsProvider wp_query={wp_query}>
        <SingleProductConsumer />
      </EcommerceProductsProvider>
    </section>
  );
}